import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import ModalManager, { useModal } from '@ebay/nice-modal-react';
import { Controller, useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import { Spinner } from 'evergreen-ui';
import * as routes from 'pages/routes';
import * as userResource from 'resources/user/hooks';
import * as userHelpers from 'resources/user/user.helpers';
import userService from 'resources/user/user.service';
import Modal from 'UI/Modal';
import Input from 'UI/Input';
import Button from 'UI/Button';
import MultiSelect from 'UI/MultiSelect';
import SelectRealtorOption from 'modals/AddNewClientModal/SelectRealtorOption';
import { yupResolver } from '@hookform/resolvers/yup';
import { defaultValues, schema } from './index.form';
import styles from './styles.module.scss';


const AddNewClientModal = ModalManager.create(({
  withoutRedirect,
  selectedRealtorId: reservedRealtorId,
  onAfterAdd,
}) => {
  const modal = useModal();
  const router = useRouter();

  const { isLoading: isUserLoading, data: user } = userResource.useCurrentUser();
  const { isLoading: isTeamMembersLoading, data: teamMembers } = userResource.useTeamMembers(user?.company_id);
  const { isLoading: isClientCreating, mutateAsync: addNewClient } = userResource.useAddNewClient();

  const {
    handleSubmit, reset, formState: { errors }, control, setError,
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(schema),
    defaultValues,
  });

  const isLoading = isUserLoading || isTeamMembersLoading;
  const isCoordinatorOrAdmin = userHelpers.isCoordinatorOrAdmin(user);
  const isRealtor = userHelpers.isRealtor(user);

  const handleFormSubmit = async (values) => {
    const realtorId = reservedRealtorId || values.realtor;

    const isUserExists = values.email && await userService.checkIsUserExists(values.email);
    if (isUserExists) {
      setError('email', { type: 'custom', message: 'User with this email has already been registered.' });
      return;
    }

    const newClient = await addNewClient({
      ...values,
      companyId: user.company_id,
      realtorId: isRealtor ? user.id : realtorId,
    });

    onAfterAdd?.(newClient);

    modal.hide();

    if (!withoutRedirect) {
      router.push({
        pathname: routes.path.clientProfile,
        query: { id: newClient.id },
      });
    }
  };

  const realtorsSelectOptions = useMemo(() => {
    const membersToMap = [...(teamMembers || [])];

    // move me at the top of list
    const meIndex = membersToMap.findIndex((member) => member.id === user.id);
    membersToMap.splice(meIndex, 1);
    membersToMap.unshift(user);

    if (isRealtor) {
      return [];
    }

    return (
      membersToMap?.map((realtor) => {
        const isMe = realtor.id === user.id;

        return {
          label: `${realtor.first_name} ${realtor.last_name} ${realtor.email}`, // label search by
          // label to display by itemRenderer
          labelInList: ({ onSelect, selected }) => (
            <SelectRealtorOption
              firstName={realtor.first_name}
              lastName={realtor.last_name}
              email={realtor.email}
              onSelect={onSelect}
              selected={selected}
              isMe={isMe}
            />
          ),
          // label to display when selected
          selectedLabel: `${realtor.first_name} ${realtor.last_name} ${isMe ? '(me)' : ''}`,
          value: realtor.id,
        };
      }) || []
    );
  }, [isRealtor, teamMembers, user]);

  // reset form with fetched values
  useEffect(() => {
    if (realtorsSelectOptions?.length) {
      reset({
        ...defaultValues,
        realtor: reservedRealtorId || realtorsSelectOptions[0]?.value,
      });
    }
  }, [realtorsSelectOptions, reservedRealtorId, reset]);

  return (
    <Modal
      isOpen={modal.visible}
      title="Add a Client"
      subtitle="Complete this form to add a buyer or seller. You can invite them later."
      onClose={modal.hide}
      onAfterClose={modal.remove}
    >
      {isLoading && (
        <div className={styles.loader}>
          <Spinner />
        </div>
      )}

      {!isLoading && (
        <form onSubmit={handleSubmit(handleFormSubmit)} className={styles.addNewClientForm}>
          <Input
            name="firstName"
            label="First Name*"
            control={control}
            error={errors.firstName}
          />

          <Input
            name="lastName"
            label="Last Name*"
            control={control}
            error={errors.lastName}
          />

          <Input
            name="email"
            label="Email Address"
            control={control}
            error={errors.email}
          />

          <Input
            type="phoneNumber"
            name="phoneNumber"
            label="Mobile Phone Number"
            control={control}
            error={errors.phoneNumber}
          />

          {isCoordinatorOrAdmin && (
            <Controller
              name="realtor"
              control={control}
              render={({ field: { onChange, value } }) => (
                <MultiSelect
                  label="Select Agent for this Client*"
                  placeholder="Select"
                  filterPlaceholder="Search"
                  options={realtorsSelectOptions}
                  onSelect={(option) => onChange(option.value)}
                  selected={reservedRealtorId || value || realtorsSelectOptions[0]?.value}
                  isMultiSelect={false}
                  className={styles.multiSelectTarget}
                  itemRenderer={({ item: { labelInList: Option }, onSelect, isSelected }) => (
                    <Option onSelect={onSelect} selected={isSelected} />
                  )}
                  disabled={!!reservedRealtorId}
                />
              )}
            />
          )}

          <div className={styles.actionButtons}>
            <Button type="default" onClick={modal.hide}>
              back
            </Button>

            <Button htmlType="submit" loading={isClientCreating}>
              add new client
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
});

AddNewClientModal.propTypes = {
  withoutRedirect: PropTypes.bool,
  selectedRealtorId: PropTypes.string,
  onAfterAll: PropTypes.func,
};

AddNewClientModal.defaultProps = {
  withoutRedirect: false,
  selectedRealtorId: null,
  onAfterAdd: () => {},
};

export default AddNewClientModal;
